import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { format } from 'date-fns';

import { useRouter } from 'src/hooks/useRouter';
import { routesMap } from 'src/routes';
import {
  CertificateGroup,
  ProductCodesCell,
  certificateDocTypeLabels,
  certificateStatusLabels,
  certificateStatusColor,
  useGetCertificate,
  verifyCertificate,
} from 'src/entities/certificate';

const formatDay = (value: string | null) =>
  value ? format(new Date(value), 'yyyy-MM-dd') : '—';

const formatWhen = (value: string | null) =>
  value ? format(new Date(value), 'yyyy-MM-dd HH:mm') : '—';

export const CertificatePage: React.FunctionComponent = () => {
  const { push, query } = useRouter();

  const certificateIdParam = query.certificateId;
  const certificateIdRaw = Array.isArray(certificateIdParam)
    ? certificateIdParam[0]
    : certificateIdParam;
  const certificateId = parseInt(certificateIdRaw || '', 10) || null;

  const certificatesRequest = useGetCertificate(certificateId);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const rawResponse = certificatesRequest.data;
  const payload = rawResponse?.data ?? null;
  const certificate: CertificateGroup | null =
    payload && 'id' in payload ? (payload as CertificateGroup) : null;

  const errors = rawResponse?.errors || [];

  const handleVerify = async () => {
    if (!certificateId || verifying) return;

    setVerifying(true);
    setVerifyError(null);

    try {
      await verifyCertificate(certificateId);
      await certificatesRequest.mutate();
    } catch (error) {
      setVerifyError(
        'Не удалось проверить сертификаты. Попробуйте ещё раз.',
      );
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={() => push(routesMap.certificates.getRoute(), {})}
          >
            Назад к списку
          </Button>
          <Button
            variant="contained"
            onClick={handleVerify}
            disabled={verifying || !certificateId}
            startIcon={
              verifying ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Обновить
          </Button>
        </Box>
        {verifyError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {verifyError}
          </Alert>
        )}
        {errors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {errors.map((error, index) => (
              <Box key={index}>{error}</Box>
            ))}
          </Alert>
        )}
        {certificatesRequest.isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        ) : !certificate ? (
          <Typography sx={{ py: 2 }} color="text.secondary">
            Сертификат не найден
          </Typography>
        ) : (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 1,
                mb: 1,
              }}
            >
              <Typography variant="subtitle1">
                {certificateDocTypeLabels[certificate.docType] ||
                  certificate.docType}
                {' · '}
                {certificate.number}
              </Typography>
              <Chip
                size="small"
                label={
                  certificateStatusLabels[certificate.status] ||
                  certificate.status
                }
                color={certificateStatusColor(certificate.status)}
              />
            </Box>
            {certificate.statusDetail && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {certificate.statusDetail}
              </Typography>
            )}
            {certificate.issueReason && certificate.actionHint && (
              <Alert severity="info" sx={{ mb: 1 }}>
                {certificate.actionHint}
              </Alert>
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 0.5,
                mb: 1,
              }}
            >
              <Box>Источник: {certificate.source}</Box>
              <Box>Действует с: {formatDay(certificate.validFrom)}</Box>
              <Box>Действует по: {formatDay(certificate.validUntil)}</Box>
              <Box>
                Проверен:{' '}
                {certificate.verifiedAt
                  ? formatWhen(certificate.verifiedAt)
                  : 'не проверялся'}
              </Box>
              <Box>
                Следующая проверка: {formatWhen(certificate.nextCheckAt)}
              </Box>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Все товары документа
              </Typography>
              <ProductCodesCell certificate={certificate} collapsible={false} />
            </Box>
            {certificate.officialLink && (
              <Link
                href={certificate.officialLink}
                target="_blank"
                rel="noreferrer"
              >
                Официальная запись в реестре
              </Link>
            )}
          </Paper>
        )}
      </Paper>
    </Box>
  );
};
